import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
// custom
import { usePageLayout } from '@/app/router';
import { ROUTES } from '@/app/router/routes';
import { useDnaQuestionFlow } from '../hooks/useDnaQuestionFlow';
import Dna1Image from '@shared/assets/dna/Dna1_x2.jpg';
import { DnaCloseButton, DnaNextButton } from './DnaListPage';
import { useLanguage } from '@shared/contexts/LanguageContext';

const OPTION_COLORS = ['#4A8AC4', '#B8903A', '#5E9E50'];

const DnaOptionCard = ({ text, accentColor, selected, disabled, onClick }) => {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="flex min-h-[122px] w-full overflow-hidden rounded-[10px] text-left shadow-[0px_1px_3px_rgba(0,0,0,0.06)] transition disabled:opacity-60"
      style={{ backgroundColor: selected ? '#D9F2F7' : '#FFFFFF' }}
    >
      <div className="w-1 shrink-0" style={{ backgroundColor: accentColor }} />
      <div className="flex flex-1 items-center gap-3 px-[14px] py-5">
        <div
          className="h-6 w-6 shrink-0 rounded-full border-2"
          style={{ borderColor: selected ? accentColor : '#D0CBC2',
            backgroundColor: selected ? accentColor : '#FFFFFF',
           }}
        />
        <span className="whitespace-pre-line text-[20px] font-medium leading-normal text-[#2A2520]">
          {text}
        </span>
      </div>
    </button>
  );
};

//---------------------------------------------------------------------------
const DnaQuestionPage = () => {
  const navigate = useNavigate();
  const { setLayoutOptions } = usePageLayout();
  const { t } = useLanguage();
  const [selectedOptionId, setSelectedOptionId] = useState(null);

  const {
    loading,
    error,
    total,
    currentIndex,
    currentQuestion,
    saving,
    reload,
    chooseOption,
  } = useDnaQuestionFlow();

  useEffect(() => {
    setLayoutOptions({ hideHeader: true, hideBottomNav: true, hideFooter: true });
    return () => setLayoutOptions({});
  }, [setLayoutOptions]);

  //---------------------------------------------------------------------------
  function onClickBack() {
    navigate(-2);
  };
  
  const onClickOption = (optionId) => {
    if (saving) return;
    setSelectedOptionId(optionId);
  };

  const onClickNext = async () => {
    if (saving || selectedOptionId == null) return;
    const result = await chooseOption(selectedOptionId);
    if (result?.done && result.recordId != null) {
      navigate(ROUTES.DNA_ANSWER, {
        state: { recordId: result.recordId },
        replace: true,
      });
      return;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setSelectedOptionId(null);
  };

  if (loading) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center gap-3 px-4 text-[#6E625B]">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#907654] border-t-transparent" />
        <p className="text-sm">{t('dna-question.loadingQuestions')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 px-4 text-center">
        <p className="text-sm text-[#6E625B]">{t('dna-question.loadError')}</p>
        <button
          type="button"
          onClick={() => reload()}
          className="rounded-xl bg-[#907654] px-6 py-2.5 text-sm font-medium text-white"
        >
          {t('dna-question.reload')}
        </button>
      </div>
    );
  }

  if (!currentQuestion) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center px-4 text-sm text-[#6E625B]">
        {t('dna-question.noQuestions')}
      </div>
    );
  }

  const currentStep = currentIndex + 1;
  const stepLabel = `${currentStep} / ${total}`;
  const progressPercent = total > 0 ? Math.min((currentStep / total) * 100, 100) : 0;

  //---------------------------------------------------------------------------
  return (
    <section
      className="relative mx-auto min-h-[932px] w-full max-w-[430px] overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #ECFCFF 0%, #F7F8FA 100%)' }}
    >
      <img src={Dna1Image} alt="" className="absolute inset-0 h-full w-full object-cover" />

      <div className="relative z-10 flex min-h-[932px] flex-col px-6 pb-6 pt-8">
        <DnaCloseButton onClick={onClickBack} />

        <div className="mt-6 border-b border-[#E2DDD6] pb-4">
          <p className="text-center text-[8px] tracking-[0.75em] text-white">PERSONALITY QUIZ · {t('dna-question.quizEyebrow')}</p>
          <h1 className="mt-2 text-center text-[35px] font-black leading-10 text-[#1A1714]">{t('dna-question.title')}</h1>
        </div>

        <div className='flex mt-8 items-start bg-[#f000]'>
        <div className="flex items-end bg-[#8000]">
          <div className="flex items-end leading-none bg-[#00f0]">
            <span className="inline-block min-w-[56px] align-bottom text-[112px] font-bold text-[#4DC8E0]">
              {currentStep}
            </span>
            <span className="inline-block min-w-[36px] text-right text-2xl font-light text-[#C8C0B4] bg-[#00ff0000]">
              /{total}
            </span>
          </div>
        </div>
        <div className='flex mt-10 bg-[#0f00]'>
          <p className="ml-4 whitespace-pre-line text-2xl font-medium leading-[38px] text-[#6E625B]">
            {currentQuestion.content}
          </p>
        </div>
        </div>

        <ul className="mt-6 flex flex-col gap-[10px]">
          {(currentQuestion.options ?? []).map((opt, index) => (
            <li key={opt.option_id}>
              <DnaOptionCard
                text={String(opt.content).replace(/\n/g, '')}
                accentColor={OPTION_COLORS[index % OPTION_COLORS.length]}
                selected={selectedOptionId === opt.option_id}
                disabled={saving}
                onClick={() => onClickOption(opt.option_id)}
              />
            </li>
          ))}
        </ul>

        <div className="mt-auto pt-6 pb-3">
          <p className="text-lg font-light text-[#B0A898]">{t('dna-question.progressLabel')}</p>
          <div className="mt-1 h-[3px] w-full rounded-sm bg-[#E2DDD6]">
            <div
              className="h-[3px] rounded-sm bg-[#4A8AC4] transition-all duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>

        <DnaNextButton
          data-layer={`"Rectangle 4407" "Vector 439"`}
          disabled={saving || selectedOptionId == null}
          aria-hidden
          onClick={() => onClickNext()}
          visibility={selectedOptionId === null}
        />

        <p className="sr-only">{stepLabel}</p>
      </div>
    </section>
  );
};

export default DnaQuestionPage;
